import { createRouter, createWebHistory } from 'vue-router'
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth'
import { collection, getDocs, limit, query, where } from 'firebase/firestore'
import { dbFirestore } from '@/firebase.config'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: () => import('../views/HomeView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/sessions',
      name: 'sessions',
      component: () => import('../views/SessionsView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/songs',
      name: 'songs',
      component: () => import('../views/SongRepertoire.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/events',
      name: 'events',
      component: () => import('../views/Events.vue'),
      meta: { requiresAuth: false },
      children: [
        {
          path: '',
          name: 'events-today',
          component: () => import('../views/Events.vue'),
        },
        {
          path: 'proximos',
          name: 'events-upcoming',
          component: () => import('../views/Events.vue'),
        },
        {
          path: 'pasados',
          name: 'events-past',
          component: () => import('../views/Events.vue'),
        },
        {
          path: ':id(\\d+)',
          name: 'event-details',
          component: () => import('../views/Events.vue'),
          props: true
        }
      ]
    },
    {
      path: '/asistencias',
      name: 'asistencias',
      component: () => import('../views/Asistencias.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'assistance_manager'] }
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/Profile.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/chat',
      name: 'chat',
      component: () => import('../views/ChatView.vue'),
      meta: { requiresAuth: true, roles: ['can_chat'] }
    },
    {
      path: '/grupos',
      name: 'grupos',
      component: () => import('../views/Grupos.vue'),
      meta: { requiresAuth: true, roles: ['admin',] }
    },
    {
      path: '/mis-grupos',
      name: 'mis-grupos',
      component: () => import('../views/Grupos.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'group_leader'] }
    },
    {
      path: '/usuarios',
      name: 'usuarios',
      component: () => import('../views/Usuarios.vue'),
      meta: { requiresAuth: true, roles: ['admin'] }
    },
    {
      path: '/ubicaciones',
      name: 'ubicaciones',
      component: () => import('../views/Ubicaciones.vue'),
      meta: { requiresAuth: true, roles: ['admin'] }
    }
  ],
})

const getCurrentUser = (): Promise<User | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

// Guard de navegación
router.beforeEach(async (to, from, next) => {
  const user = await getCurrentUser();
  const requiresAuth = to.meta.requiresAuth;
  const requiredRoles = to.meta.roles as string[] | undefined;

  // Redirigir si no está autenticado y la ruta lo requiere
  if (requiresAuth && !user) {
    return next({ name: 'Home' });
  }

  // Verificar roles si la ruta los requiere
  if (user && requiredRoles && requiredRoles.length > 0) {
    try {
      const userQuery = query(collection(dbFirestore, 'users'), where('uid', '==', user.uid), limit(1));
      const userQuerySnapshot = await getDocs(userQuery);

      if (userQuerySnapshot.empty) {
        console.warn(`Router guard: User document not found for uid: ${user.uid}.`);
        return next({ name: 'Home' });
      }

      const userData = userQuerySnapshot.docs[0].data();
      const userRoles: string[] = userData.roles || [];

      const hasPermission = requiredRoles.some(role => userRoles.includes(role));

      if (hasPermission) {
        return next();
      } else {
        console.warn(`Router guard: User lacks required roles. Required: ${requiredRoles}, User has: ${userRoles}`);
        return next({ name: 'Home' });
      }
    } catch (error) {
      console.error('Router guard: Error fetching user roles.', error);
      return next({ name: 'Home' });
    }
  }

  // Si no hay condiciones especiales, permitir el paso
  return next();
});

export default router
